import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Cluster, FormField, Input, Modal, Stack } from "@salesapp/ui";
import { billingApi, type AiAddonState, type AiBilling, type AiOffer } from "../lib/billingApi";
import { moneyLabel } from "../lib/planPricing";
import { safePayfastCheckoutUrl } from "../lib/returnTo";
import { useLocale } from "../shell/LocaleProvider";

function submitPayfastCheckout(processUrl: string, fields: Record<string, string>) {
  const form = document.createElement("form");
  form.method = "post";
  form.action = processUrl;
  form.style.display = "none";
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

function termLabel(months: number): string {
  return months === 12 ? "12 months" : `${months} month${months === 1 ? "" : "s"}`;
}

function seatsLabel(quantity: number): string {
  return `${quantity} seat${quantity === 1 ? "" : "s"}`;
}

/** Loxley is priced per seat; this spells out the monthly amount for every seat together. */
export function seatTotalLine(line: AiOffer["lines"][number], currency: string): string {
  const monthly = moneyLabel(line.monthly_cents * line.quantity, currency);
  return `${monthly} a month for all ${seatsLabel(line.quantity)} together.`;
}

const PAID_BY: Record<string, string> = {
  saved_card: "Saved card",
  checkout: "PayFast",
  dev_fixture: "Test payment",
};

/** AI services billing: LeadGen, Loxley and Mailbox AI are priced by Saleslox per organisation, outside the CRM plan. */
export function AiBillingSection({ orgId, locked }: { orgId: string; locked: boolean }) {
  const { formatDate, formatDateTime } = useLocale();
  const [data, setData] = useState<AiBilling | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [paying, setPaying] = useState<AiOffer | null>(null);
  const [ack, setAck] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returned = params.get("ai_payment");
    if (returned === "paid") {
      setNotice("Payment received. Your AI services switch on as soon as PayFast confirms it.");
    } else if (returned === "cancelled") {
      setNotice("Payment cancelled. The offer is still waiting for you below.");
    }
    if (returned) {
      params.delete("ai_payment");
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", next);
    }
    billingApi
      .aiBilling(orgId)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "AI services billing could not be loaded."));
  }, [orgId]);

  function close() {
    if (busy) return;
    setPaying(null);
    setAck(false);
    setChallengeId(null);
    setCode("");
    setModalError(null);
  }

  async function openPay(offer: AiOffer) {
    setPaying(offer);
    setAck(false);
    setCode("");
    setModalError(null);
    setBusy(true);
    try {
      const started = await billingApi.startVerification(orgId, "plan_change");
      setChallengeId(started.challenge_id);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "The confirmation code could not be sent.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmAndPay() {
    if (!paying || !challengeId) return;
    if (!ack) {
      setModalError("Tick the confirmation before paying.");
      return;
    }
    setBusy(true);
    setModalError(null);
    try {
      const confirmed = await billingApi.confirmVerification(orgId, {
        purpose: "plan_change",
        challenge_id: challengeId,
        code,
      });
      const result = await billingApi.acceptAiOffer(orgId, paying.id, {
        verification_token: confirmed.verification_token,
        acknowledged: true,
      });
      if (result.status === "checkout") {
        const processUrl = safePayfastCheckoutUrl(result.process_url);
        if (!processUrl || !result.fields) throw new Error("PayFast checkout could not be started.");
        submitPayfastCheckout(processUrl, result.fields);
        return;
      }
      setData(result);
      setPaying(null);
      setNotice("Paid. Your AI services are on.");
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "The payment could not be completed.");
    } finally {
      setBusy(false);
    }
  }

  function status(addon: AiAddonState): string {
    if (addon.active) return addon.expires_at ? `Active until ${formatDate(addon.expires_at)}` : "Active";
    if (addon.switched_on && addon.expires_at) return `Expired ${formatDate(addon.expires_at)}`;
    return "Not on";
  }

  const pending = data?.pending ?? null;

  return (
    <Stack gap={4}>
      <hr className="billing-section-divider" />
      <Stack gap={2}>
        <h2 className="home-section-title" id="ai-billing">AI services</h2>
        <p className="home-subtitle">
          LeadGen, the Loxley AI assistant and Mailbox AI are added to your plan and priced separately. Contact
          sales for a quote.
        </p>
      </Stack>
      {error ? <p className="crm-error" role="alert">{error}</p> : null}
      {notice ? <p className="home-subtitle" role="status">{notice}</p> : null}
      {data ? (
        <Card>
          <table className="billing-payment-history" aria-label="AI services">
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.addons.map((addon) => (
                <tr key={addon.addon}>
                  <td>{addon.label}</td>
                  <td>{status(addon)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
      {pending ? (
        <Card>
          <Stack gap={3}>
            <Stack gap={1}>
              <strong>Offer from Saleslox</strong>
              <p className="home-subtitle">
                {termLabel(pending.term_months)} from the day you pay
                {pending.pushed_at ? `, sent ${formatDate(pending.pushed_at)}` : ""}.
              </p>
              {pending.note ? <p>{pending.note}</p> : null}
            </Stack>
            <table className="billing-payment-history" aria-label="Offer">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Per month</th>
                  <th>Quantity</th>
                  <th>{termLabel(pending.term_months)}</th>
                </tr>
              </thead>
              <tbody>
                {pending.lines.map((line) => (
                  <tr key={line.addon}>
                    <td>
                      {line.label}
                      {line.addon === "loxley" ? (
                        <p className="home-subtitle">{seatTotalLine(line, pending.currency)}</p>
                      ) : null}
                    </td>
                    <td>
                      {moneyLabel(line.monthly_cents, pending.currency)}
                      {line.addon === "loxley" ? " per seat" : ""}
                    </td>
                    <td>{line.addon === "loxley" ? seatsLabel(line.quantity) : line.quantity}</td>
                    <td>{moneyLabel(line.term_cents, pending.currency)}</td>
                  </tr>
                ))}
                {pending.tax_cents > 0 ? (
                  <tr>
                    <td colSpan={3}>{pending.tax_label ?? "Tax"} ({pending.tax_percent}%)</td>
                    <td>{moneyLabel(pending.tax_cents, pending.currency)}</td>
                  </tr>
                ) : null}
                <tr>
                  <td colSpan={3}><strong>Total</strong></td>
                  <td><strong>{moneyLabel(pending.total_cents, pending.currency)}</strong></td>
                </tr>
              </tbody>
            </table>
            <Cluster gap={3} align="center">
              <Button type="button" disabled={locked} onClick={() => void openPay(pending)}>
                Accept and pay
              </Button>
              <span className="home-subtitle">
                {data?.has_saved_card
                  ? "Charged to the card saved for your CRM plan."
                  : "You pay once on PayFast. Nothing renews by itself."}
              </span>
            </Cluster>
          </Stack>
        </Card>
      ) : data ? (
        <p className="home-subtitle">No AI services offer is waiting. Contact sales to add one to your plan.</p>
      ) : null}
      {data ? (
        <Stack gap={3}>
          <h3 className="home-section-title">AI payment history</h3>
          {data.history.length === 0 ? (
            <p className="home-subtitle">No AI payments yet.</p>
          ) : (
            <Card>
              <table className="billing-payment-history" aria-label="AI payment history">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Services</th>
                    <th>Term</th>
                    <th>Amount</th>
                    <th>Paid by</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((row) => (
                    <tr key={row.id}>
                      <td>{row.paid_at ? formatDateTime(row.paid_at) : "—"}</td>
                      <td>{row.lines.map((line) => line.label).join(", ")}</td>
                      <td>{termLabel(row.term_months)}</td>
                      <td>{moneyLabel(row.total_cents, row.currency)}</td>
                      <td>{row.payment_method ? PAID_BY[row.payment_method] ?? row.payment_method : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </Stack>
      ) : null}
      <Modal
        isOpen={Boolean(paying)}
        onClose={close}
        title="Pay for AI services"
        footer={(
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={close}>Cancel</Button>
            <Button type="button" disabled={busy || !ack || !challengeId} onClick={() => void confirmAndPay()}>
              {busy ? "Working…" : "Confirm and pay"}
            </Button>
          </>
        )}
      >
        <Stack gap={4}>
          {modalError ? <p className="crm-error" role="alert">{modalError}</p> : null}
          {paying ? (
            <Checkbox
              label={`I agree to pay ${moneyLabel(paying.total_cents, paying.currency)} for ${termLabel(paying.term_months)} of AI services.`}
              checked={ack}
              onChange={(event) => setAck(event.target.checked)}
            />
          ) : null}
          <FormField
            label="Confirmation code"
            htmlFor="ai-billing-verify-code"
            hint="We emailed a 6-digit code. It expires in 5 minutes."
          >
            <Input
              id="ai-billing-verify-code"
              className="verify-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          </FormField>
        </Stack>
      </Modal>
    </Stack>
  );
}
